/**
 * Auth API endpoints
 */

import { api } from '../client';
import type {
  LoginRequest,
  RegisterRequest,
  RegisterResult,
  ResendVerificationRequest,
  SetPasswordRequest,
  VerifyEmailRequest,
  VerifyEmailResult,
} from '../types';
import type { AuthResponse } from '../types';
import {
  AuthResponseSchema,
  OAuthAuthorizeResponseSchema,
  RegisterResponseSchema,
  ResendVerificationResponseSchema,
  TokenResponseSchema,
  VerifyEmailResponseSchema,
} from '../schemas';
import { parseResponse } from '../validation';

// Auth endpoints that set/clear the __session refresh cookie MUST use
// `credentials: 'include'`. The client.ts default is 'omit' (performance —
// avoids sending cookies on every API call), but 'omit' also makes the browser
// DISCARD Set-Cookie headers on the response. Without 'include', login
// silently succeeds but the refresh cookie is never stored.
const AUTH_COOKIE_OPTIONS = { credentials: 'include' as const };

export const authApi = {
  login: (data: LoginRequest) =>
    api.post('/auth/login', data, AUTH_COOKIE_OPTIONS).then(
      (res) => parseResponse(AuthResponseSchema, res, 'POST /auth/login'),
    ),

  // Register resolves to one of two shapes depending on whether the env
  // requires email verification. The api client does not surface the HTTP
  // status (201 vs 202), so we discriminate on the body: the 202 carries a
  // `status:"verification_sent"` literal, the 201 carries `access_token`. The
  // union schema makes the branch type-safe; the caller reads `verificationSent`.
  register: (data: RegisterRequest): Promise<RegisterResult> =>
    api.post('/auth/register', data, AUTH_COOKIE_OPTIONS).then((res) => {
      const parsed = parseResponse(RegisterResponseSchema, res, 'POST /auth/register');
      return 'status' in parsed
        ? { verificationSent: true, email: parsed.email }
        : { verificationSent: false, auth: parsed };
    }),

  /** Set the password an emailed link proves — the invitation's landing
   *  action. A FRESH consumption answers the AuthResponse and sets the same
   *  cookies as login (hence AUTH_COOKIE_OPTIONS). The refusals are the
   *  link's own states: 410 a dead or replaced link, 409 a password already
   *  set, 403 the password IS set but the account's period has not begun
   *  (the problem's detail names the start date). */
  setPassword: (data: SetPasswordRequest): Promise<AuthResponse> =>
    api.post('/auth/password', data, AUTH_COOKIE_OPTIONS).then((res) =>
      parseResponse(AuthResponseSchema, res, 'POST /auth/password')),

  /** Verify an email via the raw token from the link. Resolves to one of two
   *  shapes, mirroring register: a FRESH consumption returns an AuthResponse
   *  and also sets the refresh/access cookies (same as login, hence
   *  AUTH_COOKIE_OPTIONS); a REPLAY of an already-consumed token returns a
   *  `status:"already_verified"` body with no session (see
   *  AlreadyVerifiedResponse) — the account is verified but this call does
   *  NOT log the caller in. A bad/expired/unknown token surfaces as a 410
   *  ApiError. */
  verifyEmail: (data: VerifyEmailRequest): Promise<VerifyEmailResult> =>
    api.post('/auth/verify-email', data, AUTH_COOKIE_OPTIONS).then((res) => {
      const parsed = parseResponse(VerifyEmailResponseSchema, res, 'POST /auth/verify-email');
      return 'status' in parsed
        ? { verified: false }
        : { verified: true, auth: parsed };
    }),

  /** Re-send a verification link. Always 200 generic (no account enumeration);
   *  the caller learns only that the request was accepted. */
  resendVerification: (data: ResendVerificationRequest) =>
    api.post('/auth/resend-verification', data).then(
      (res) => parseResponse(ResendVerificationResponseSchema, res, 'POST /auth/resend-verification'),
    ),

  /** Refresh returns only access token. Refresh token is in httpOnly cookie. */
  refresh: () =>
    api.post('/auth/refresh', undefined, AUTH_COOKIE_OPTIONS).then(
      (res) => parseResponse(TokenResponseSchema, res, 'POST /auth/refresh'),
    ),

  logout: () =>
    api.post<undefined>('/auth/logout', undefined, AUTH_COOKIE_OPTIONS),

  /** Get OAuth authorization URL + CSRF state for a provider (Google, Microsoft). */
  oauthAuthorize: (provider: string) =>
    api.get(`/auth/oauth/${provider}/authorize`).then(
      (res) => parseResponse(OAuthAuthorizeResponseSchema, res, `GET /auth/oauth/${provider}/authorize`),
    ),

  /** Exchange OAuth callback code for user session. Uses AUTH_COOKIE_OPTIONS to set the __session refresh cookie. */
  oauthCallback: (provider: string, code: string, state: string) =>
    api.post(`/auth/oauth/${provider}/callback`, { code, state }, AUTH_COOKIE_OPTIONS).then(
      (res) => parseResponse(AuthResponseSchema, res, `POST /auth/oauth/${provider}/callback`),
    ),
};
